// White-label site footer, the pure config model + resolver, split out so it's
// unit-testable and archetype-agnostic. Lives under Venue.appConfig.footer (an
// additive JSON key, no schema/API change). Defaults are resolved HERE at
// RENDER time from data the venue already has, so simply shipping the footer
// writes NOTHING to any venue and can never overwrite an existing site. A venue
// only gets custom footer data once it (or a seed) explicitly sets
// appConfig.footer via a deep-merge. See components/site-footer.tsx.

export type FooterLink = { label: string; href: string };
export type FooterAddress = { label?: string; lines: string[] };
export type FooterSocials = {
  instagram?: string;
  facebook?: string;
  twitter?: string;
  tiktok?: string;
  youtube?: string;
  spotify?: string;
  bandcamp?: string;
  website?: string;
};

/** Per-venue footer config (appConfig.footer). All optional, absent = sensible defaults. */
export type FooterConfig = {
  enabled?: boolean; // default true, every site gets a footer
  addresses?: FooterAddress[]; // e.g. a venue's one or more locations
  links?: FooterLink[]; // policy / contact links
  socials?: FooterSocials;
  showMailingList?: boolean; // default true, reuses the venue's own list
  mailingListHeading?: string; // default "Mailing list"
  /** Named list on this venue to add footer signups to (default: primary list). */
  mailingListId?: string;
  copyrightName?: string; // default the venue name
  tagline?: string; // optional line under the copyright
  showAssemblyCredit?: boolean; // default FALSE, white-label; opt-in credit only
};

export type ResolvedFooter = {
  enabled: boolean;
  addresses: FooterAddress[];
  links: FooterLink[];
  socials: FooterSocials;
  showMailingList: boolean;
  mailingListHeading: string;
  mailingListId?: string;
  copyright: string;
  tagline?: string;
  showAssemblyCredit: boolean;
};

/** Only keep social handles that are actually set. */
function cleanSocials(s: FooterSocials | undefined): FooterSocials {
  const out: FooterSocials = {};
  for (const [k, v] of Object.entries(s ?? {})) {
    if (typeof v === "string" && v.trim()) out[k as keyof FooterSocials] = v.trim();
  }
  return out;
}

/**
 * Resolve the footer for a venue. `cfg` is the venue's appConfig.footer (may be
 * absent). Defaults come from data the venue already owns (name, year), so a
 * venue with no footer config still renders a clean, on-brand footer, with
 * zero writes to the DB.
 */
export function resolveFooter(
  venue: { name?: string | null; appName?: string | null },
  cfg: FooterConfig | null | undefined,
  now: Date,
): ResolvedFooter {
  const f = cfg ?? {};
  const brand = (f.copyrightName || venue.appName || venue.name || "").trim();
  return {
    enabled: f.enabled !== false,
    addresses: (f.addresses ?? []).filter((a) => a && Array.isArray(a.lines) && a.lines.some((l) => l?.trim())),
    links: (f.links ?? []).filter((l) => l && l.label?.trim() && l.href?.trim()),
    socials: cleanSocials(f.socials),
    showMailingList: f.showMailingList !== false,
    mailingListHeading: f.mailingListHeading?.trim() || "Mailing list",
    mailingListId: f.mailingListId?.trim() || undefined,
    copyright: `© ${now.getFullYear()}${brand ? ` ${brand}` : ""}. All rights reserved.`,
    tagline: f.tagline?.trim() || undefined,
    // White-label: platform credit is OFF unless a venue explicitly opts in.
    showAssemblyCredit: f.showAssemblyCredit === true,
  };
}

/** Is there anything worth rendering beyond the copyright line? */
export function footerHasContent(f: ResolvedFooter): boolean {
  return (
    f.addresses.length > 0 ||
    f.links.length > 0 ||
    Object.keys(f.socials).length > 0 ||
    f.showMailingList
  );
}
